import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { resolveBirthLocation } from "@/utils/calcTools/astro/geoUtils";
import { convertText } from "@/utils/langConvert";
import BirthTimePicker from "@/pages/profileManage/components/BirthTimePicker";
import BirthPlacePicker from "@/pages/profileManage/components/BirthPlacePicker";
import type { CardData, BirthFormData } from "../types";
import { calcTrueSolarTime, toStoredBirthTime, formatCoord, resolveFormBirthLocation } from "../utils";

interface BirthInfoFormProps {
  data: CardData;
  isLight: boolean;
  formData: BirthFormData;
  setFormData: React.Dispatch<React.SetStateAction<BirthFormData>>;
}

export default function BirthInfoForm({
  data: _data,
  isLight,
  formData,
  setFormData,
}: BirthInfoFormProps) {
  const [showBirthPicker, setShowBirthPicker] = useState(false);
  const [showBirthPlacePicker, setShowBirthPlacePicker] = useState(false);
  const [showCurrentResidencePicker, setShowCurrentResidencePicker] = useState(false);
  const { i18n } = useTranslation();
  const trd = (text: string) => convertText(text, i18n.language);

  const birthLocation = useMemo(() => {
    return resolveFormBirthLocation(formData);
  }, [formData]);

  const trueSolarTime = useMemo(() => {
    if (!birthLocation || !formData.birthTime) return "--";
    return calcTrueSolarTime(
      toStoredBirthTime(formData.birthTime),
      birthLocation.lat,
      birthLocation.lng,
      birthLocation.tz,
    );
  }, [birthLocation, formData.birthTime]);

  const birthCoordDisplay = useMemo(() => {
    if (!birthLocation) return "--";
    return formatCoord(birthLocation.lat, birthLocation.lng);
  }, [birthLocation]);

  const residenceLocation = useMemo(() => {
    if (!formData.currentResidence) return null;
    const resolved = resolveBirthLocation(formData.currentResidence);
    return resolved.isDefault ? null : resolved;
  }, [formData.currentResidence]);

  const residenceCoordDisplay = useMemo(() => {
    if (!residenceLocation) return "--";
    return formatCoord(residenceLocation.lat, residenceLocation.lng);
  }, [residenceLocation]);

  const rowBase = `flex items-center justify-between px-4 py-3.5 rounded-xl border text-sm md:text-sm transition-colors cursor-pointer ${
    isLight
      ? "bg-[#fafafa] border-[#e5e5ea] hover:border-[#d1d1d6]"
      : "bg-cosmic-900 border-white/10 hover:border-white/20"
  }`;

  const labelCls = isLight ? "text-[#6e6e73]" : "text-silver-400";
  const inputCls = `bg-transparent text-right outline-none placeholder:text-[#c5c5c7] w-full ${
    isLight ? "text-[#1c1c1e]" : "text-white"
  }`;
  const chevronCls = isLight ? "text-[#c5c5c7]" : "text-silver-600";

  return (
    <div className="space-y-2.5">
      {/* 姓名 */}
      <label className={rowBase}>
        <span className={`${labelCls} whitespace-nowrap flex-shrink-0`}>{trd("姓名")}</span>
        <input
          type="text"
          placeholder={trd("请输入姓名")}
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          className={inputCls}
        />
      </label>

      {/* 性别 */}
      <div className={rowBase}>
        <span className={labelCls}>{trd("性别")}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              setFormData((prev) => ({ ...prev, gender: "male" }))
            }
            className={`px-4 py-1.5 rounded-full text-xs md:text-xs font-medium transition-colors cursor-pointer ${
              formData.gender === "male"
                ? isLight
                  ? "bg-[#4a4a4c] text-white"
                  : "bg-gold-500/20 text-gold-400 border border-gold-500/30"
                : isLight
                  ? "bg-[#f0f0f5] text-[#6e6e73] border border-[#e5e5ea]"
                  : "bg-cosmic-900 text-silver-500 border border-white/10"
            }`}
          >
            {trd("男")}
          </button>
          <button
            onClick={() =>
              setFormData((prev) => ({ ...prev, gender: "female" }))
            }
            className={`px-4 py-1.5 rounded-full text-xs md:text-xs font-medium transition-colors cursor-pointer ${
              formData.gender === "female"
                ? isLight
                  ? "bg-[#4a4a4c] text-white"
                  : "bg-gold-500/20 text-gold-400 border border-gold-500/30"
                : isLight
                  ? "bg-[#f0f0f5] text-[#6e6e73] border border-[#e5e5ea]"
                  : "bg-cosmic-900 text-silver-500 border border-white/10"
            }`}
          >
            {trd("女")}
          </button>
        </div>
      </div>

      {/* 出生时间 */}
      <div className={rowBase} onClick={() => setShowBirthPicker(true)}>
        <div className="flex items-center gap-2">
          <span className={labelCls}>{trd("出生时间")}</span>
          <span
            className={`text-xs md:text-[10px] px-1.5 py-0.5 rounded ${
              isLight
                ? "bg-red-50 text-red-500 border border-red-100"
                : "bg-red-500/15 text-red-400 border border-red-500/20"
            }`}
          >
            {trd("必填")}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-1 justify-end">
          <span
            className={`text-xs md:text-xs truncate ${
              formData.birthTime
                ? isLight
                  ? "text-[#1c1c1e]"
                  : "text-white"
                : isLight
                  ? "text-[#c5c5c7]"
                  : "text-silver-600"
            }`}
          >
            {formData.birthTime ? toStoredBirthTime(formData.birthTime) : trd("请选择")}
          </span>
          <i className={`ri-arrow-right-s-line ${chevronCls}`} />
        </div>
      </div>

      {/* 出生地区 */}
      <div className={rowBase} onClick={() => setShowBirthPlacePicker(true)}>
        <span className={labelCls}>{trd("出生地区")}</span>
        <div className="flex items-center gap-1 flex-1 justify-end">
          <span
            className={`text-xs md:text-xs truncate ${
              formData.birthPlace
                ? isLight
                  ? "text-[#1c1c1e]"
                  : "text-white"
                : isLight
                  ? "text-[#c5c5c7]"
                  : "text-silver-600"
            }`}
          >
            {formData.birthPlace ? trd(formData.birthPlace) : trd("请选择")}
          </span>
          <i className={`ri-arrow-right-s-line ${chevronCls}`} />
        </div>
      </div>

      {/* 现居住地 */}
      <div className={rowBase} onClick={() => setShowCurrentResidencePicker(true)}>
        <span className={labelCls}>{trd("现居住地")}</span>
        <div className="flex items-center gap-1 flex-1 justify-end">
          {formData.currentResidence && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFormData((prev) => ({ ...prev, currentResidence: "" }));
              }}
              className={`w-4 h-4 flex items-center justify-center rounded-full ${
                isLight ? "bg-[#f0f0f5] text-[#9e9ea2]" : "bg-white/10 text-silver-500"
              }`}
            >
              <i className="ri-close-line text-[10px]" />
            </button>
          )}
          <span
            className={`text-xs md:text-xs truncate ${
              formData.currentResidence
                ? isLight
                  ? "text-[#1c1c1e]"
                  : "text-white"
                : isLight
                  ? "text-[#c5c5c7]"
                  : "text-silver-600"
            }`}
          >
            {formData.currentResidence ? trd(formData.currentResidence) : trd("请选择")}
          </span>
          <i className={`ri-arrow-right-s-line ${chevronCls}`} />
        </div>
      </div>

      {/* 底部信息栏 */}
      <div
        className={`px-4 py-3 rounded-xl border text-xs md:text-xs space-y-1 ${
          isLight
            ? "bg-[#fafafa] border-[#e5e5ea]"
            : "bg-cosmic-900 border-white/10"
        }`}
      >
        <div className={`flex items-center gap-2 ${labelCls}`}>
          <i className="ri-time-line text-xs md:text-xs" />
          <span>
            {trd("真太阳时：")}<span className={isLight ? "text-[#1c1c1e]" : "text-white"}>{trueSolarTime}</span>
          </span>
        </div>
        <div className={`flex items-center gap-2 ${labelCls}`}>
          <i className="ri-map-pin-line text-xs md:text-xs" />
          <span>
            {trd("出生地经纬度：")}<span className={isLight ? "text-[#1c1c1e]" : "text-white"}>{birthCoordDisplay}</span>
          </span>
        </div>
        {formData.currentResidence && (
          <div className={`flex items-center gap-2 ${labelCls}`}>
            <i className="ri-map-pin-2-line text-xs md:text-xs" />
            <span>
              {trd("居住地经纬度：")}<span className={isLight ? "text-[#1c1c1e]" : "text-white"}>{residenceCoordDisplay}</span>
            </span>
          </div>
        )}
      </div>

      {showBirthPicker && (
        <BirthTimePicker
          value={formData.birthTime}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, birthTime: value }));
            if (!formData.birthPlace) {
              setShowBirthPlacePicker(true);
            }
          }}
          onClose={() => setShowBirthPicker(false)}
        />
      )}

      {showBirthPlacePicker && (
        <BirthPlacePicker
          value={formData.birthPlace}
          onChange={(value) => setFormData((prev) => ({
            ...prev,
            birthPlace: value,
          }))}
          onClose={() => setShowBirthPlacePicker(false)}
        />
      )}

      {showCurrentResidencePicker && (
        <BirthPlacePicker
          value={formData.currentResidence}
          onChange={(value) => setFormData((prev) => ({ ...prev, currentResidence: value }))}
          onClose={() => setShowCurrentResidencePicker(false)}
          title={trd("现居住地")}
          syncFrom={formData.birthPlace}
        />
      )}
    </div>
  );
}
